import { motion, useMotionValue, useSpring } from "framer-motion";
import { Instagram, Sparkles } from "lucide-react";
import { RevealLine } from "@/components/RevealLine";

const SCREENSHOTS = [
  { src: "/app-screenshot-1.webp", label: "Equal Spacings calculator" },
  { src: "/app-screenshot-2.webp", label: "Footings Spacing calculator" },
  { src: "/app-screenshot-3.webp", label: "Raked Walls calculator" },
  { src: "/app-screenshot-4.webp", label: "Stud Measurements cut list" },
  { src: "/app-screenshot-5.webp", label: "Concrete Quantities calculator" },
  { src: "/app-screenshot-6.webp", label: "NCC Digital Books" },
  { src: "/app-screenshot-7.webp", label: "Daily Building Logs" },
  { src: "/app-screenshot-8.webp", label: "Blank Sketch drafting tool" },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, delay, ease: [0.76, 0, 0.24, 1] },
});

export default function Home() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const px = useSpring(mx, { stiffness: 55, damping: 18 });
  const py = useSpring(my, { stiffness: 55, damping: 18 });

  const handleMouseMove = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 22);
    my.set((e.clientY / window.innerHeight - 0.5) * 16);
  };

  return (
    <main data-testid="home-page" onMouseMove={handleMouseMove} className="relative z-10">
      <section className="flex min-h-[100dvh] flex-col items-center justify-center px-6 py-10 text-center">
        <div className="flex flex-col items-center">
        <motion.div style={{ x: px, y: py }} className="overflow-visible">
          <div className="overflow-hidden">
            <motion.img
              data-testid="hero-logo"
              src="/chippymate-logo.png"
              alt="Offcut logo"
              initial={{ y: "108%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
              className="w-44 drop-shadow-[0_20px_45px_rgba(8,2,30,0.45)] sm:w-64 md:w-80"
            />
          </div>
        </motion.div>

        <div data-testid="offcut-title" className="mt-4 sm:mt-5">
          <div className="overflow-hidden">
            <motion.img
              src="/offcut-title.png"
              alt="OFFCUT"
              initial={{ y: "112%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.3, ease: [0.76, 0, 0.24, 1] }}
              className="w-56 sm:w-72 lg:w-96"
            />
          </div>
        </div>

        <p
          data-testid="hero-tagline"
          className="mt-4 text-xs font-semibold uppercase tracking-[0.25em] text-white/60 sm:text-sm"
        >
          <RevealLine delay={0.5}>Developed and owned by ChippyMate Pty Ltd</RevealLine>
        </p>

        <p
          data-testid="hero-motto"
          className="mt-10 max-w-xs text-sm font-semibold leading-relaxed text-white/90 sm:max-w-2xl sm:text-lg"
        >
          <RevealLine delay={0.65}>
            Discover formulas, digital logbooks, drafting tools and
            ever-evolving features designed to help you work smarter, faster,
            and more efficient.
          </RevealLine>
        </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-8 text-center sm:px-10">
        <motion.h2
          {...fadeUp()}
          data-testid="ultimate-heading"
          className="font-brand text-5xl tracking-wide text-white sm:text-6xl"
        >
          The Ultimate Building App.
        </motion.h2>
        <motion.p
          {...fadeUp(0.1)}
          className="mx-auto mt-4 max-w-2xl text-sm font-semibold leading-relaxed text-white/90 sm:text-lg"
        >
          Everything you need to build, create, and get the job done.
        </motion.p>
        <motion.div
          {...fadeUp(0.15)}
          className="mx-auto mt-8 max-w-2xl space-y-4 text-sm leading-relaxed text-white/70 sm:text-base"
        >
          <p>
            Offcut brings the tools tradies reach for every day into one app.
            Work out equal spacings, footings, raked walls, stud measurements
            and concrete quantities in seconds with built-in formulas and
            calculators. Keep every job organised with digital logbooks and
            daily building logs, flick through the NCC with digital books and
            bookmarks, and sketch plans with drafting tools that save straight
            to your pocket.
          </p>
          <p>
            Running a crew? Premium Team shares bookmarks, drafts and daily
            logs across up to five members with one simple bill, and Ultimate
            Team rolls job, delivery and trade hours up for the boss with seats
            tailored to your business.
          </p>
          <p>
            Built by tradies who got sick of digging through folders for a
            formula and losing logbook pages on site, Offcut is designed around
            real jobs — quick answers when you need them, records that stay
            organised, and tools that hold up in the real world. New features
            land regularly, shaped by what tradies actually ask for.
          </p>
        </motion.div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-14 sm:px-10">
        <motion.p
          {...fadeUp()}
          className="mx-auto mb-10 max-w-2xl text-center text-sm leading-relaxed text-white/70 sm:text-base"
        >
          See it in action — real screens from the app, from quick on-site
          calculations to drafting, standards and daily logs. Every tool is
          built to save you time on site and keep your paperwork in one place.
        </motion.p>
        <div
          data-testid="screenshots-grid"
          className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4"
        >
          {SCREENSHOTS.map((shot, i) => (
            <motion.div
              key={shot.src}
              {...fadeUp(i * 0.1)}
              className="overflow-hidden rounded-2xl border border-white/10 bg-black/30 shadow-[0_20px_50px_rgba(8,2,30,0.35)]"
            >
              <img
                data-testid={`screenshot-${i + 1}`}
                src={shot.src}
                alt={`Offcut app — ${shot.label}`}
                loading="lazy"
                className="h-auto w-full transition-transform duration-500 hover:scale-[1.03]"
              />
            </motion.div>
          ))}
        </div>
      </section>

      <section className="mx-auto flex max-w-4xl flex-col items-center px-6 py-14 text-center sm:px-10">
        <motion.span
          {...fadeUp()}
          data-testid="trial-badge"
          className="inline-flex items-center gap-2 rounded-full border border-[#07B9CE]/50 bg-[#07B9CE]/15 px-6 py-2.5 text-sm font-bold tracking-wide text-[#5ee5f2]"
        >
          <Sparkles className="h-4 w-4" />
          Start your 7-day free trial
        </motion.span>
        <motion.p
          {...fadeUp(0.05)}
          className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base"
        >
          Try everything free for seven days, then choose the plan that fits —
          Pro for the individual chippy, or Premium Team for the boss and crew.
          Simple monthly or yearly pricing in Australian dollars, GST included,
          and you can cancel anytime. Download Offcut on Google Play or the App
          Store and put it to work on your next job.
        </motion.p>

        <motion.div
          {...fadeUp(0.1)}
          className="mt-8 flex flex-wrap items-center justify-center gap-4 sm:gap-6"
        >
          <img
            data-testid="google-play-badge"
            src="/badge-google-play.png"
            alt="Get it on Google Play"
            className="w-40 drop-shadow-[0_10px_25px_rgba(8,2,30,0.35)] transition-transform duration-300 hover:scale-105 sm:w-48"
          />
          <img
            data-testid="app-store-badge"
            src="/badge-app-store.png"
            alt="Download on the App Store"
            className="w-40 drop-shadow-[0_10px_25px_rgba(8,2,30,0.35)] transition-transform duration-300 hover:scale-105 sm:w-48"
          />
        </motion.div>

        <motion.a
          {...fadeUp(0.2)}
          data-testid="instagram-link"
          href="https://www.instagram.com/chippymate.co"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Follow Offcut on Instagram"
          className="group mt-10 flex items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
        >
          <span className="flex flex-col items-start leading-none">
            <span className="text-[10px] font-bold tracking-[0.32em] text-white/70 transition-colors duration-300 group-hover:text-white/90">
              FOLLOW US ON
            </span>
            <span className="mt-1 font-['Yellowtail'] text-3xl leading-none text-white">
              instagram
            </span>
          </span>
          <Instagram
            className="h-8 w-8 text-white transition-transform duration-300 group-hover:rotate-6 group-hover:scale-110"
            strokeWidth={1.75}
          />
          <span className="text-sm font-semibold text-white/60 transition-colors duration-300 group-hover:text-white/90">
            @chippymate.co
          </span>
        </motion.a>
      </section>

      <footer className="border-t border-white/10 px-6 py-8 text-center">
        <p
          data-testid="footer-copyright"
          className="text-xs font-medium tracking-wider text-white/60"
        >
          &copy; 2026 ChippyMate ACN:702 485 005. All rights reserved.
        </p>
      </footer>
    </main>
  );
}
